'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { clientPageTitle, normalizeTitle, TITLE_NAME_META } from '@/lib/seo/pageTitles';

/**
 * Keeps the browser tab title in the language chosen with the switcher. The
 * server always renders the Ukrainian title; this re-applies it in English when
 * needed, and again when Next swaps <head> on navigation (metadata can stream
 * in after this effect). Compares normalised text so it never writes in a loop.
 */
export default function DocumentTitleSync() {
  const pathname = usePathname();
  const { language } = useLanguage();

  useEffect(() => {
    const apply = () => {
      if (window.location.pathname !== pathname) return;
      const name = document.querySelector(`meta[name="${TITLE_NAME_META}"]`)?.getAttribute('content') ?? null;
      const title = clientPageTitle(pathname, language, name);
      if (title && normalizeTitle(document.title) !== normalizeTitle(title)) document.title = title;
    };
    apply();
    const observer = new MutationObserver(apply);
    observer.observe(document.head, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, [pathname, language]);

  return null;
}
